import type { ReactNode } from 'react';
import { APP_NAME } from '@sincronia/shared';
import { AVATAR_STYLES, GLYPH_PATHS, cn, initials } from '../lib/ui.js';
import type { AvatarKey } from '@sincronia/shared';

/** Marca da aplicação: pulsos concêntricos, identidade própria. */
export function BrandMark({
  size = 48,
  animated = true,
}: {
  size?: number;
  animated?: boolean;
}): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={APP_NAME}
      className="shrink-0"
    >
      <defs>
        <radialGradient id="brand-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#eafaff" />
          <stop offset="55%" stopColor="var(--color-pulse-500)" />
          <stop offset="100%" stopColor="var(--color-nova-500)" />
        </radialGradient>
      </defs>
      <circle
        cx="32"
        cy="32"
        r="26"
        fill="none"
        stroke="var(--color-nova-500)"
        strokeOpacity="0.3"
        strokeWidth="2"
      />
      <circle
        cx="32"
        cy="32"
        r="18"
        fill="none"
        stroke="var(--color-pulse-500)"
        strokeOpacity="0.5"
        strokeWidth="2"
      />
      <circle
        cx="32"
        cy="32"
        r="11"
        fill="none"
        stroke="var(--color-pulse-400)"
        strokeOpacity="0.85"
        strokeWidth="2.5"
        className={
          animated ? 'origin-center animate-[pulse-ring_4.5s_ease-out_infinite]' : undefined
        }
      />
      <circle cx="32" cy="32" r="5.5" fill="url(#brand-core)" />
    </svg>
  );
}

export function Wordmark({ className }: { className?: string }): ReactNode {
  return (
    <span
      className={cn(
        'bg-gradient-to-r from-pulse-400 to-nova-400 bg-clip-text text-transparent',
        className,
      )}
    >
      {APP_NAME}
    </span>
  );
}

/** Fundo abstrato com pulsos. Respeita prefers-reduced-motion via CSS. */
export function PulseBackground(): ReactNode {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="animate-drift absolute -top-1/4 left-1/2 size-[120vmax] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(124,92,255,0.14)_0%,transparent_60%)]" />
      <div className="animate-drift absolute -bottom-1/3 -left-1/4 size-[90vmax] rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.10)_0%,transparent_62%)] [animation-delay:-6s]" />
      <div className="absolute inset-0 bg-[repeating-linear-gradient(115deg,transparent_0_38px,rgba(255,255,255,0.012)_38px_39px)]" />
    </div>
  );
}

/** Avatar do jogador: cor + forma (nunca apenas cor). */
export function PlayerAvatar({
  avatarKey,
  name,
  size = 32,
  dimmed = false,
}: {
  avatarKey: AvatarKey;
  name: string;
  size?: number;
  dimmed?: boolean;
}): ReactNode {
  const style = AVATAR_STYLES[avatarKey] ?? AVATAR_STYLES.aurora;
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full',
        dimmed && 'opacity-45 saturate-50',
      )}
      style={{
        width: size,
        height: size,
        background: `color-mix(in srgb, ${style.color} 22%, transparent)`,
        border: `1.5px solid ${style.color}`,
      }}
      title={`${name} (${style.label})`}
    >
      <svg
        width={size * 0.52}
        height={size * 0.52}
        viewBox="0 0 24 24"
        aria-hidden="true"
        style={{ fill: style.color }}
      >
        <path d={GLYPH_PATHS[style.glyph]} />
      </svg>
      <span className="sr-only">{`${name} — ${style.label}`}</span>
    </span>
  );
}

export function PlayerInitials({ name }: { name: string }): ReactNode {
  return <span aria-hidden="true">{initials(name)}</span>;
}
